"use client";

import { motion } from "framer-motion";
import { Users, ShieldCheck, Briefcase, Heart, UsersRound, PartyPopper, Route } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
interface TravelerScenario {
  id: string;
  title: string;
  desc: string;
  icon: LucideIcon;
}
const travelerScenarios: TravelerScenario[] = [{
  id: "families-with-children",
  title: "Families With Children",
  desc: "Door-to-door transfers with space for your family and luggage.",
  icon: Users
}, {
  id: "baby-child-seats",
  title: "Child Seats",
  desc: "Child seats are available free of charge for children aged one year and older. Please include your child's age in your transfer request.",
  icon: ShieldCheck
}, {
  id: "large-luggage",
  title: "Large Luggage",
  desc: "Tell us about golf clubs, diving equipment, or oversized bags so we can confirm a suitable vehicle.",
  icon: Briefcase
}, {
  id: "couples",
  title: "Couples",
  desc: "Private, quiet, and climate-controlled direct journeys without shared shuttle stops.",
  icon: Heart
}, {
  id: "groups",
  title: "Groups",
  desc: "Transport for groups travelling together. We will confirm a suitable vehicle for your group.",
  icon: UsersRound
}, {
  id: "wedding-parties",
  title: "Wedding Parties",
  desc: "Ask us about transport arrangements for wedding guests at Red Sea resorts.",
  icon: PartyPopper
}, {
  id: "long-distance-routes",
  title: "Long-Distance Routes",
  desc: "Comfortable intercity transit to Luxor, Cairo, and Aswan with experienced highway drivers.",
  icon: Route
}];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.04
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 16
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.45,
      ease: "easeOut"
    }
  }
};
export default function TravelCare() {
  return <section id="travel-made-easy-for-everyone" data-nav="light" className="bg-background text-foreground py-20 lg:py-24 border-b border-border">
      <div className="mx-auto max-w-[1136px] px-6">
        {/* Header Block */}
        <div className="mb-12 lg:mb-14">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xs font-semibold uppercase tracking-wider text-primary">
              Customized Transfer Care
            </span>
            <span className="inline-block h-1 w-1 rounded-full bg-primary" aria-hidden="true" />
            <div className="h-px w-10 bg-border" aria-hidden="true" />
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-black font-default">
            Travel Made Easy for Everyone
          </h2>
        </div>

        {/* 7-Item Responsive Mosaic Grid */}
        <motion.div variants={containerVariants} initial="hidden" animate="visible" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-6">
          {travelerScenarios.map((item, index) => {
          // Balanced 12-column mosaic distribution for 7 items:
          // Row 1: 4 cards x col-span-3 (3+3+3+3 = 12)
          // Row 2: 3 cards x col-span-4 (4+4+4 = 12)
          const spanClass = index < 4 ? "lg:col-span-3" : "lg:col-span-4";
          const Icon = item.icon;
          const isAccent = item.id === "baby-child-seats" || item.id === "wedding-parties";
          return <motion.div key={item.id} data-index={index} variants={itemVariants} className={spanClass}>
                <Card data-index={index} className="h-full rounded-md border border-border bg-card shadow-xs transition-[border-color,box-shadow] duration-200 hover:border-primary/60 hover:shadow-md overflow-hidden flex flex-col justify-between py-0">
                  <div>
                    {/* Icon Badge */}
                    <div className="pt-6 px-5">
                      <div className={`size-12 sm:size-14 rounded-lg flex items-center justify-center ${isAccent ? "bg-[#D99000]/10 border border-[#D99000]/25 text-[#D99000]" : "bg-primary/10 border border-primary/25 text-primary"}`}>
                        <Icon className="size-6" />
                      </div>
                    </div>

                    <CardHeader className="pt-5 pb-2 px-5">
                      <CardTitle className="text-base lg:text-lg font-semibold tracking-tight text-black font-default">
                        {item.title}
                      </CardTitle>
                    </CardHeader>

                    <CardContent className="px-5 pb-5">
                      <p className="text-sm text-muted-foreground leading-relaxed font-default">
                        {item.desc}
                      </p>
                    </CardContent>
                  </div>
                </Card>
              </motion.div>;
        })}
        </motion.div>
      </div>
    </section>;
}